/**
 * Fantasy Console 0 - Console Wrapper Component
 *
 * Embeds the console iframe and manages its lifecycle.
 */

import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useContext,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { ConsoleHost } from '@fc0/console'
import { ErrorPayload, Cart } from '@fc0/types'
import { CartContext } from '../../context/CartContext'
import { useConsole } from '../../context/ConsoleContext'
import { ConsoleControls } from './ConsoleControls'
import { ErrorDisplay } from '../StatusBar/ErrorDisplay'
import { ConsoleStatus } from '../../types'
import styles from './ConsoleWrapper.module.css'

export interface ConsoleWrapperRef {
  reload: () => void
  pause: () => void
  resume: () => void
  reset: () => void
  pauseResume: () => void
  setMuted: (muted: boolean) => void
}

type ConsoleSize =
  | 'small'
  | 'medium'
  | 'large'
  | 'xLarge'
  | 'jumbo'
  | 'fullscreen'

interface ConsoleWrapperProps {
  size?: ConsoleSize
  showControls?: boolean
  autoLoad?: boolean
  autoReloadOnCodeChange?: boolean
  onError?: (error: ErrorPayload) => void
  /** When provided, use this cart directly instead of CartContext */
  cart?: Cart
  /** When provided with cart prop, used for reload tracking */
  cartLoadVersion?: number
  /** Show boot animation before loading cart (play page/tab only) */
  showBootAnimation?: boolean
}

const SIZE_MAP: Record<ConsoleSize, number | string> = {
  small: 256,
  medium: 384,
  large: 512,
  xLarge: 640,
  jumbo: 768,
  fullscreen: '100%',
}

export const ConsoleWrapper = forwardRef<
  ConsoleWrapperRef,
  ConsoleWrapperProps
>(function ConsoleWrapper(
  {
    size = 'medium',
    showControls = true,
    autoLoad = true,
    autoReloadOnCodeChange = true,
    onError: onErrorProp,
    cart: cartProp,
    cartLoadVersion: cartLoadVersionProp,
    showBootAnimation,
  },
  ref,
) {
  const cartCtx = useContext(CartContext)
  const cart = cartProp ?? cartCtx?.cart
  const cartLoadVersion = cartLoadVersionProp ?? cartCtx?.cartLoadVersion ?? 0

  // When showBootAnimation is true, cart can be undefined initially (loaded async).
  // Otherwise, cart is required.
  if (!cart && !showBootAnimation) {
    throw new Error(
      'ConsoleWrapper requires either a cart prop or CartProvider',
    )
  }
  const { registerCapture, unregisterCapture } = useConsole()
  const containerRef = useRef<HTMLDivElement>(null)
  const hostRef = useRef<ConsoleHost | null>(null)
  const isFirstLoad = useRef(true)
  const bootStarted = useRef(false)
  const [status, setStatus] = useState<ConsoleStatus>('loading')
  const [error, setError] = useState<ErrorPayload | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [isMuted, setIsMuted] = useState(false)

  const loadCart = useCallback(() => {
    if (!hostRef.current || !cart) return

    isFirstLoad.current = false

    setError(null)
    hostRef.current.loadCart(cart).catch((err) => {
      const errorPayload = { message: err.message }
      setError(errorPayload)
      setStatus('error')
      onErrorProp?.(errorPayload)
    })
  }, [cart, onErrorProp])

  const loadCartRef = useRef(loadCart)
  useEffect(() => {
    loadCartRef.current = loadCart
  }, [loadCart])

  const handleReload = useCallback(() => {
    loadCart()
  }, [loadCart])

  const handlePause = useCallback(() => {
    hostRef.current?.pause()
    setIsPaused(true)
    setStatus('paused')
  }, [])

  const handleResume = useCallback(() => {
    hostRef.current?.resume()
    setIsPaused(false)
    setStatus('running')
  }, [])

  const handleReset = useCallback(() => {
    hostRef.current?.reset()
    setError(null)
    setIsPaused(false)
    setStatus('running')
  }, [])

  const handlePauseResume = useCallback(() => {
    if (isPaused) {
      handleResume()
    } else {
      handlePause()
    }
  }, [isPaused, handlePause, handleResume])

  const handleSetMuted = useCallback((muted: boolean) => {
    hostRef.current?.setMuted(muted)
    setIsMuted(muted)
  }, [])

  const handleMuteToggle = useCallback(() => {
    handleSetMuted(!isMuted)
  }, [isMuted, handleSetMuted])

  useImperativeHandle(
    ref,
    () => ({
      reload: handleReload,
      pause: handlePause,
      resume: handleResume,
      reset: handleReset,
      pauseResume: handlePauseResume,
      setMuted: handleSetMuted,
    }),
    [
      handleReload,
      handlePause,
      handleResume,
      handleReset,
      handlePauseResume,
      handleSetMuted,
    ],
  )

  useEffect(() => {
    if (!containerRef.current) return

    const host = new ConsoleHost(containerRef.current, {
      iframeSrc: '/console-iframe.html',
    })

    hostRef.current = host

    // Register capture function for thumbnail generation
    registerCapture(() => host.captureCanvas())

    host.onReady(() => {
      setStatus('ready')
      if (autoLoad) {
        if (showBootAnimation && isFirstLoad.current) {
          // Start boot animation immediately (no cart needed)
          bootStarted.current = true
          host.startBoot()
        } else {
          loadCartRef.current()
        }
      }
    })

    host.onCartLoaded(() => {
      setStatus('running')
      setIsPaused(false)
    })

    host.onError((err) => {
      setError(err)
      setStatus('error')
      onErrorProp?.(err)
    })

    host.onFrozen(() => {
      setStatus('error')
    })

    host.onLog((log) => {
      if (log.level === 'error') {
        console.error('[Console]', log.message)
      } else if (log.level === 'warn') {
        console.warn('[Console]', log.message)
      } else {
        console.log('[Console]', log.message)
      }
    })

    return () => {
      unregisterCapture()
      host.destroy()
      hostRef.current = null
    }
  }, [registerCapture, unregisterCapture])

  // When cart becomes available during boot-first flow, send it to the runtime.
  // The runtime will queue it if boot is still playing, or load immediately if boot is done.
  useEffect(() => {
    if (!cart || !hostRef.current || !showBootAnimation || !isFirstLoad.current)
      return
    if (!bootStarted.current) return
    loadCart()
  }, [cart, showBootAnimation, loadCart])

  // Track code files, sprites, and map for change detection
  const codeFilesString = cart ? JSON.stringify(cart.codeFiles) : ''

  // Hot-reload sprite changes without restarting game
  useEffect(() => {
    if (status === 'running' || status === 'paused') {
      if (cart?.sprites) {
        hostRef.current?.updateSprites(cart.sprites)
      }
    }
  }, [cart?.sprites])

  // Hot-reload map changes without restarting game
  useEffect(() => {
    if (status === 'running' || status === 'paused') {
      if (cart?.map) {
        hostRef.current?.updateMap(cart.map)
      }
    }
  }, [cart?.map])

  // Hot-reload SFX changes without restarting game
  useEffect(() => {
    if (status === 'running' || status === 'paused') {
      if (cart?.sfx) {
        hostRef.current?.updateSFX(cart.sfx)
      }
    }
  }, [cart?.sfx])

  // Reload on CODE changes - only if enabled (default true for backward compat)
  useEffect(() => {
    if (
      (status === 'running' || status === 'paused') &&
      autoReloadOnCodeChange
    ) {
      loadCart()
    }
  }, [codeFilesString, autoReloadOnCodeChange])

  // Reload when a different cart is loaded (covers all console states)
  const prevCartLoadVersion = useRef(cartLoadVersion)
  useEffect(() => {
    if (prevCartLoadVersion.current === cartLoadVersion) return
    prevCartLoadVersion.current = cartLoadVersion
    loadCart()
  }, [cartLoadVersion])

  const dimension = SIZE_MAP[size]
  const containerStyle =
    size === 'fullscreen'
      ? { width: '100%', height: '100%' }
      : { width: dimension, height: dimension }

  return (
    <div className={styles.wrapper}>
      <div
        ref={containerRef}
        className={styles.console}
        style={containerStyle}
      />
      {error && <ErrorDisplay error={error} onDismiss={() => setError(null)} />}
      {showControls && (
        <ConsoleControls
          status={status}
          isPaused={isPaused}
          isMuted={isMuted}
          onReload={handleReload}
          onPauseResume={handlePauseResume}
          onReset={handleReset}
          onMuteToggle={handleMuteToggle}
        />
      )}
    </div>
  )
})
